import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Copy, Play, Info, Sparkles, BookOpen } from "lucide-react";
import { MCP_TOOLS, type McpToolDef } from "./mcpToolCatalog";

function ToolRunner({ tool }: { tool: McpToolDef }) {
  const [values, setValues] = useState<Record<string, any>>({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; data?: any; error?: string; ms?: number } | null>(null);

  const setField = (name: string, v: any) => setValues((prev) => ({ ...prev, [name]: v }));

  const onRun = async () => {
    setRunning(true);
    const started = performance.now();
    try {
      const res = await tool.run(values);
      setResult({ ...res, ms: Math.round(performance.now() - started) });
      if (res.ok) toast.success(`${tool.name} ran successfully`);
      else toast.error(res.error || `${tool.name} failed`);
    } catch (e: any) {
      setResult({ ok: false, error: e?.message ?? String(e), ms: Math.round(performance.now() - started) });
      toast.error(e?.message ?? "Tool crashed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg font-mono">{tool.name}</CardTitle>
              <Badge variant={tool.kind === "write" ? "destructive" : "secondary"}>
                {tool.kind === "write" ? "Write" : "Read"}
              </Badge>
            </div>
            <CardDescription className="mt-1">{tool.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {tool.fields.length === 0 && (
          <p className="text-sm text-muted-foreground italic">This tool takes no arguments.</p>
        )}
        {tool.fields.map((f) => (
          <div key={f.name} className="space-y-1.5">
            <Label htmlFor={`${tool.name}-${f.name}`} className="text-sm">
              {f.label} {f.required && <span className="text-destructive">*</span>}
              <span className="ml-2 text-xs text-muted-foreground font-mono">{f.name}</span>
            </Label>
            {f.type === "text" ? (
              <Textarea
                id={`${tool.name}-${f.name}`}
                placeholder={f.placeholder}
                value={values[f.name] ?? ""}
                onChange={(e) => setField(f.name, e.target.value)}
                rows={3}
              />
            ) : f.type === "boolean" ? (
              <div className="flex items-center gap-2">
                <Switch
                  id={`${tool.name}-${f.name}`}
                  checked={Boolean(values[f.name])}
                  onCheckedChange={(v) => setField(f.name, v)}
                />
                <span className="text-sm text-muted-foreground">{values[f.name] ? "Yes" : "No"}</span>
              </div>
            ) : (
              <Input
                id={`${tool.name}-${f.name}`}
                type={f.type === "number" ? "number" : "text"}
                placeholder={f.placeholder}
                value={values[f.name] ?? ""}
                onChange={(e) => setField(f.name, e.target.value)}
              />
            )}
            {f.description && <p className="text-xs text-muted-foreground">{f.description}</p>}
          </div>
        ))}

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={onRun} disabled={running} className="gap-2">
            <Play className="h-4 w-4" />
            {running ? "Running…" : "Run tool"}
          </Button>
          {result && (
            <span className="text-xs text-muted-foreground">
              {result.ok ? "Success" : "Error"} · {result.ms}ms
            </span>
          )}
        </div>

        {result && (
          <div className="mt-2">
            {!result.ok ? (
              <Alert variant="destructive">
                <AlertTitle>Tool error</AlertTitle>
                <AlertDescription className="font-mono text-xs whitespace-pre-wrap">{result.error}</AlertDescription>
              </Alert>
            ) : (
              <div className="rounded-md border bg-muted/40">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-xs font-semibold uppercase text-muted-foreground">Result</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(result.data, null, 2));
                      toast.success("Copied result JSON");
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy JSON
                  </Button>
                </div>
                <pre className="p-3 text-xs overflow-auto max-h-96 whitespace-pre-wrap break-words">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CatalogCard({ tool }: { tool: McpToolDef }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-lg font-mono">{tool.name}</CardTitle>
          <Badge variant={tool.kind === "write" ? "destructive" : "secondary"}>
            {tool.kind === "write" ? "Write" : "Read"}
          </Badge>
          <span className="text-sm text-muted-foreground">{tool.title}</span>
        </div>
        <CardDescription className="mt-2">{tool.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tool.fields.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Arguments</p>
            <ul className="space-y-1 text-sm">
              {tool.fields.map((f) => (
                <li key={f.name} className="flex gap-2">
                  <code className="font-mono text-xs bg-muted rounded px-1.5 py-0.5">{f.name}</code>
                  <span className="text-muted-foreground text-xs">
                    ({f.type}
                    {f.required ? ", required" : ""}) {f.description ?? f.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Sample prompts (paste into any MCP-connected agent)
          </p>
          <ul className="space-y-2">
            {tool.samplePrompts.map((p, i) => (
              <li key={i} className="group flex items-start gap-2 rounded-md border bg-muted/30 p-2">
                <span className="flex-1 text-sm">{p}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 opacity-70 group-hover:opacity-100 gap-1.5"
                  onClick={() => {
                    navigator.clipboard.writeText(p);
                    toast.success("Prompt copied");
                  }}
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MCPConsole() {
  const [tab, setTab] = useState<"catalog" | "console">("catalog");
  const readTools = useMemo(() => MCP_TOOLS.filter((t) => t.kind === "read"), []);
  const writeTools = useMemo(() => MCP_TOOLS.filter((t) => t.kind === "write"), []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Agent Integrations (MCP)</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Browse and test the OFUESI MCP tools that agents (ChatGPT, Claude, Cursor, Lovable) can call. The catalog
          documents every tool; the console lets you run each tool with real data.
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Where to connect an MCP client</AlertTitle>
        <AlertDescription className="text-sm">
          The OFUESI MCP server is exposed via the Supabase Edge Function{" "}
          <code className="font-mono text-xs">mcp</code>. Point any MCP client at{" "}
          <code className="font-mono text-xs">
            https://ruamgnhtybcahvtzwtem.functions.supabase.co/mcp
          </code>{" "}
          to expose these tools. In Lovable, open <strong>Agent integrations</strong> from the ⋯ menu (or{" "}
          <kbd className="rounded border px-1 text-xs">Cmd</kbd>+<kbd className="rounded border px-1 text-xs">K</kbd> →{" "}
          "Agent integrations").
        </AlertDescription>
      </Alert>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="catalog" className="gap-2">
            <BookOpen className="h-4 w-4" /> Tool catalog
          </TabsTrigger>
          <TabsTrigger value="console" className="gap-2">
            <Play className="h-4 w-4" /> Tool console
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="mt-4 space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">
              Read tools ({readTools.length})
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              {readTools.map((t) => (
                <CatalogCard key={t.name} tool={t} />
              ))}
            </div>
          </section>
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">
              Write tools ({writeTools.length})
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              {writeTools.map((t) => (
                <CatalogCard key={t.name} tool={t} />
              ))}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="console" className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Each card below runs the tool against the live database using your admin session. Use this to verify tools
            behave the same way MCP-connected agents see them.
          </p>
          <div className="grid gap-4">
            {MCP_TOOLS.map((t) => (
              <ToolRunner key={t.name} tool={t} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
